import { useEffect, useState, useCallback } from 'react'

/**
 * Options for keyboard navigation hook
 */
export interface UseKeyboardNavigationOptions {
  /** Whether the dropdown is open */
  isOpen: boolean
  /** Number of items in the list */
  itemCount: number
  /** Callback when an item is selected (Enter key) */
  onSelect: (index: number) => void
  /** Callback when dropdown should close (Escape key) */
  onClose: () => void
  /** Whether keyboard navigation is enabled */
  enabled?: boolean
}

/**
 * Return type for keyboard navigation hook
 */
export interface UseKeyboardNavigationReturn {
  /** Currently highlighted item index (-1 for none) */
  highlightedIndex: number
  /** Manually set the highlighted index */
  setHighlightedIndex: (index: number) => void
  /** Reset highlighted index to -1 */
  resetHighlightedIndex: () => void
}

/**
 * Hook that handles keyboard navigation for dropdown lists
 * Supports ArrowUp, ArrowDown, Enter, and Escape keys
 *
 * @param options - Configuration options
 * @returns Navigation state and setters
 */
export const useKeyboardNavigation = ({
  isOpen,
  itemCount,
  onSelect,
  onClose,
  enabled = true,
}: UseKeyboardNavigationOptions): UseKeyboardNavigationReturn => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const resetHighlightedIndex = useCallback(() => {
    setHighlightedIndex(-1)
  }, [])

  useEffect(() => {
    if (!enabled || !isOpen || itemCount === 0) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex((prev) => (prev < itemCount - 1 ? prev + 1 : 0))
          break

        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : itemCount - 1))
          break

        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && highlightedIndex < itemCount) {
            onSelect(highlightedIndex)
          }
          break

        case 'Escape':
          e.preventDefault()
          onClose()
          setHighlightedIndex(-1)
          break

        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, itemCount, highlightedIndex, onSelect, onClose, enabled])

  return {
    highlightedIndex,
    setHighlightedIndex,
    resetHighlightedIndex,
  }
}
